import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  HttpLink,
  from,
  Observable,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import requestTracker from './requestTracker'; // Import the shared RequestTracker logic
import { CAF_URI } from '../config/envConfig';

let apolloClient = null;

export function getApolloClient() {
  if (!apolloClient) {
    throw new Error('ApolloClient has not been initialized yet.');
  }
  return apolloClient;
}

const createApolloClient = (oktaAuth) => {
  const token = oktaAuth.getAccessToken();

  // Use environment variable with fallback to empty string
  const apiUri = process.env.REACT_APP_CAF_URI || CAF_URI;
  if (!apiUri) {
    console.error('REACT_APP_CAF_URI environment variable is not set');
  }

  const primaryHttpLink = new HttpLink({
    uri: apiUri,
    headers: {
      authorization: token ? `Bearer ${token}` : '',
    },
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        // Sanitize error message to remove sensitive data
        const sanitizedMessage = message.replace(
          /Bearer\s+[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*/g,
          '[REDACTED]'
        );
        console.log(
          `[GraphQL error]: Message: ${sanitizedMessage}, Location: ${locations}, Path: ${path}`
        );
      });
    }

    if (networkError) {
      // Sanitize network error to remove sensitive data
      const sanitizedError = networkError
        .toString()
        .replace(
          /Bearer\s+[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*/g,
          '[REDACTED]'
        );
      console.log(`[Network error]: ${sanitizedError}`);
    }
  });

  const trackingLink = new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      const requestId = requestTracker.addRequest();

      const subscription = forward(operation).subscribe({
        next: (result) => {
          observer.next(result);
        },
        error: (error) => {
          requestTracker.removeRequest(requestId);
          // Sanitize error before passing it
          const sanitizedError = new Error(
            error.message.replace(
              /Bearer\s+[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*/g,
              '[REDACTED]'
            )
          );
          observer.error(sanitizedError);
        },
        complete: () => {
          requestTracker.removeRequest(requestId);
          observer.complete();
        },
      });

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          requestTracker.removeRequest(requestId);
        }
      };
    });
  });

  const opNameLink = new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      oktaAuth.tokenManager
        .get('accessToken')
        .then((accessToken) => {
          const token = accessToken?.accessToken;
          operation.setContext(({ headers }) => ({
            headers: {
              ...headers,
              authorization: token ? `Bearer ${token}` : '',
            },
          }));

          const subscription = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: (error) => {
              // Sanitize error before passing it
              const sanitizedError = new Error(
                error.message.replace(
                  /Bearer\s+[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*/g,
                  '[REDACTED]'
                )
              );
              observer.error(sanitizedError);
            },
            complete: observer.complete.bind(observer),
          });

          return () => {
            if (subscription) {
              subscription.unsubscribe();
            }
          };
        })
        .catch(() => {
          console.error('Error getting access token: [REDACTED]');
          observer.error(new Error('Authentication error'));
        });
    });
  });

  // Create a new link to add operation name as query parameter to the URL
  const operationNameLink = new ApolloLink((operation, forward) => {
    // Try to get the operation name from the operation object first
    let operationName = operation.operationName;

    // If no operation name, try to extract from the query string
    if (!operationName && operation.query) {
      const queryString = operation.query.loc?.source?.body;

      if (queryString) {
        // Extract operation type (query/mutation) and possible name
        const operationTypeMatch = queryString.match(
          /^\s*(query|mutation)\s+([\w\d_]+)?/i
        );

        if (operationTypeMatch) {
          // If there's a named operation, use it
          if (operationTypeMatch[2]) {
            operationName = operationTypeMatch[2];
          } else {
            // For anonymous operations, use a direct string-based approach
            const operationNames = [];

            // Check if this is a shorthand query (starts directly with { without query keyword)
            const isShorthandQuery = /^\s*\{/.test(queryString);

            if (isShorthandQuery) {
              // Simple extraction for shorthand queries like { getMasterDataFilters { ... } }
              const simplifiedQuery = queryString.replace(/\s+/g, ' ');
              const rootFieldMatch = simplifiedQuery.match(
                /\{\s*([a-zA-Z][a-zA-Z0-9_]*)/
              );

              if (rootFieldMatch && rootFieldMatch[1]) {
                operationName = rootFieldMatch[1];
              }
            } else {
              // For queries with the query keyword, extract operation names
              const lines = queryString.split('\n');

              // Go through each line looking for operations
              for (const line of lines) {
                // Skip comment lines and empty lines
                if (line.trim().startsWith('#') || !line.trim()) {
                  continue;
                }

                // Match operation calls: look for words followed by parenthesis
                const matches = line.match(/\b([a-zA-Z][a-zA-Z0-9_]*)\s*\(/g);

                if (matches) {
                  // Clean up the matches (remove parenthesis)
                  matches.forEach((match) => {
                    const fieldName = match.replace(/[\s(]/g, '');
                    if (fieldName && !operationNames.includes(fieldName)) {
                      operationNames.push(fieldName);
                    }
                  });
                }
              }

              if (operationNames.length > 0) {
                // Join all operation names with underscores
                operationName = operationNames.join('_');
              } else {
                // Fallback to operation type if no operations can be extracted
                operationName = operationTypeMatch[1].toLowerCase();
              }
            }
          }
        }
      }
    }

    // Only modify the request if we have an operation name
    if (operationName) {
      const context = operation.getContext();
      const { uri } = context;

      // Get the current URI or use the one from the primary link
      const currentUri = uri || primaryHttpLink.options.uri;

      // Append the operation name as a query parameter
      const separator = currentUri.includes('?') ? '&' : '?';
      const newUri = `${currentUri}${separator}operation=${operationName}`;

      operation.setContext({
        ...context,
        uri: newUri,
      });
    }

    return forward(operation);
  });

  // Ensure responses are emitted to subscribers in the same order as requests were initiated,
  // while still executing requests in parallel. This link buffers out-of-order responses and
  // releases them when all prior requests have emitted.
  const orderedEmissionLink = new ApolloLink((operation, forward) => {
    // Sequence state is kept within the client factory scope so each client instance
    // maintains its own ordering without leaking globally.
    if (!createApolloClient.__orderState) {
      createApolloClient.__orderState = {
        nextSeq: 1,
        emitPointer: 1,
        buffer: new Map(), // seq -> { type: 'next'|'error', result?, error?, observer, cancelled }
        cancelled: new Map(), // seq -> boolean
      };
    }

    const state = createApolloClient.__orderState;

    return new Observable((observer) => {
      const seq = state.nextSeq++;
      state.cancelled.set(seq, false);

      const tryFlush = () => {
        // Emit in strict sequence order
        while (state.buffer.has(state.emitPointer)) {
          const entry = state.buffer.get(state.emitPointer);
          state.buffer.delete(state.emitPointer);
          const isCancelled = state.cancelled.get(state.emitPointer);
          state.cancelled.delete(state.emitPointer);
          state.emitPointer += 1;

          // Skip notifying if the subscription was cancelled
          if (isCancelled || !entry) {
            continue;
          }

          try {
            if (entry.type === 'next') {
              entry.observer.next(entry.result);
              // Network queries usually emit once; complete after emitting
              entry.observer.complete();
            } else if (entry.type === 'error') {
              entry.observer.error(entry.error);
            }
          } catch (e) {
            // Properly handle emission exceptions by notifying the specific observer
            try {
              entry.observer.error(e);
            } catch (_) {
              // Swallow secondary errors to avoid breaking the flush loop
            }
          }
        }
      };

      const subscription = forward(operation).subscribe({
        next: (result) => {
          state.buffer.set(seq, {
            type: 'next',
            result,
            observer,
          });
          tryFlush();
        },
        error: (error) => {
          // Do minimal sanitization similar to other links
          const sanitizedError = new Error(
            (error?.message || String(error)).replace(
              /Bearer\s+[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*/g,
              '[REDACTED]'
            )
          );

          state.buffer.set(seq, {
            type: 'error',
            error: sanitizedError,
            observer,
          });
          tryFlush();
        },
        // Completion is coordinated during flush to ensure ordering
        complete: () => {},
      });

      return () => {
        if (subscription) {
          subscription.unsubscribe();
        }
        // Mark this sequence as cancelled; when it reaches the flush point, it will be skipped
        state.cancelled.set(seq, true);
      };
    });
  });

  apolloClient = new ApolloClient({
    link: from([
      errorLink,
      trackingLink,
      opNameLink,
      operationNameLink,
      // Ensure ordered emission to subscribers without serializing network calls
      orderedEmissionLink,
      primaryHttpLink,
    ]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-first', // or 'cache-and-network'
      },
    },
  });

  return apolloClient;
};

export { createApolloClient };
